<template>
  <div>
    <div class="card shadow-sm border-0 p-4 mb-4">
      <!-- Header Title -->
      <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h5 class="fw-bold mb-0 text-primary">
          <i class="bi bi-file-earmark-bar-graph me-2"></i>
          Ledger Report
        </h5>
      </div>

      <!-- Date Filters -->
      <div class="row g-3 align-items-center">
      	<div class="col-md-6">
  	        <div class="position-relative">
              <!-- Search Input -->
              <input
              type="text"
              v-model="query"
              @input="debouncedSearch"
              :placeholder="$t('search_item_by_name_code')"
              class="form-control shadow-sm rounded-pill"
              @keydown="handleKeys"
            />

              <!-- Dropdown Search Results -->
              <div
                v-if="query"
                class="search-results shadow rounded bg-white position-absolute mt-2"
                style="width:450px; max-height: 450px; overflow-y: auto; z-index: 1050; border: 1px solid #e0e0e0;"
                :class="{ 'd-block': results.length > 0, 'd-none': results.length === 0 }"
              >

              <!-- Results List -->
              <ul class="list-group list-group-flush">
                <li
                  v-for="(item, index) in results"
                  :key="item.id"
                  class="list-group-item d-flex justify-content-between align-items-center"
                  :class="{ 'bg-primary text-white': index === itemIndex }"
                  style="padding: 10px; font-size: 0.95rem; cursor: pointer;"
                >
                  <!-- Clickable Product Info -->
                  <div
                    class="flex-grow-1 d-flex flex-column"
                    @click="selectItem(item)"
                    style="cursor: pointer;"
                  >
                    <div class="d-flex align-items-center gap-2">

                      <!-- Item Info -->
                      <div class="d-flex flex-column">
                        <strong class="text-dark">{{ item.node }} - {{ item.account_id }}</strong>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>

              <!-- No Results -->
              <div v-if="!results.length" class="text-center text-muted py-4" style="font-size: 1rem;">
                No results found
              </div>
            </div>
            </div>
  	    </div>

        <div class="col-md-6">
          <label class="form-label fw-semibold small text-secondary mb-1">
            <i class="bi bi-calendar3 me-1"></i>{{ $t('start_date') }}
          </label>
          <input
            type="date"
            class="form-control shadow-sm rounded-pill"
            v-model="filters.start_date"
          />
          <div v-if="errors.start_date" class="text-danger small mt-1">
            <i class="bi bi-exclamation-circle"></i> {{ errors.start_date }}
          </div>
        </div>

        <div class="col-md-6">
          <label class="form-label fw-semibold small text-secondary mb-1">
            <i class="bi bi-calendar3 me-1"></i>{{ $t('end_date') }}
          </label>
          <input
            type="date"
            class="form-control shadow-sm rounded-pill"
            v-model="filters.end_date"
          />
          <div v-if="errors.end_date" class="text-danger small mt-1">
            <i class="bi bi-exclamation-circle"></i> {{ errors.end_date }}
          </div>
        </div>
      </div>

      <!-- Divider -->
      <hr class="text-muted my-4" />

      <!-- Buttons -->
      <div class="d-flex justify-content-start gap-3 flex-wrap">
        <button @click="submitReport" class="btn btn-outline-primary rounded-pill px-4">
          <i class="bi bi-arrow-repeat me-2"></i>  Search
        </button>  
      </div>
    </div>

    <div class="card shadow-sm border-0 p-4 mb-4">
      <!-- Header Title -->
      <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h5 class="fw-bold mb-0 text-primary">
          <i class="bi bi-file-earmark-bar-graph me-2"></i>
          Ledger Report
        </h5>
      </div>


      <div class="table-responsive">
        <table class="table table-striped caption-top">
          <caption v-if="filters.account_id" class="text-center">
            <strong class="text-dark text-uppercase">{{ selectedItem.node }}[{{ selectedItem.account_id }}]</strong>
            <br>
            <strong class="text-dark">Form: {{ filters.start_date ? dateFormat(filters.start_date) : '' }} - To: {{ filters.end_date ? dateFormat(filters.end_date) : '' }}</strong>
          </caption>
          <thead>
            <tr>
              <th>{{ $t("voucher_date") }}</th>
              <th>{{ $t("narration") }}</th>
              <th class="text-end">{{ $t("debit") }}</th>
              <th class="text-end">{{ $t("credit") }}</th>
              <th class="text-end">{{ $t("balance") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="opening">
              <td>{{ filters.start_date ? dateFormat(filters.start_date) : '' }}</td>
              <td>Opening Balance</td>
              <td class="text-end">{{opening.total_debit}}</td>
              <td class="text-end">{{opening.total_credit}}</td>
              <td class="text-end fw-bold">{{opening_balance > 0 ? `${Math.abs(opening_balance)}Dr` : `${Math.abs(opening_balance)}Cr`}}</td>
            </tr>
            <tr v-for="legder in ledgers">
              <td>{{ legder.voucher_date }}</td>
              <td>{{ legder.narration }}</td>
              <td class="text-end">{{ legder.debit }}</td>
              <td class="text-end">{{ legder.credit }}</td>
              <td class="text-end fw-bold">{{ legder.balance > 0 ? `${Math.abs(legder.balance)}Cr` : `${Math.abs(legder.balance)}Dr`}}</td>
            </tr>
          </tbody>
        </table>

      </div>
    </div>
  </div>
</template>


<script>
import apiUrls from "@/urls";
import { useToast } from 'vue-toastification';
import feather from "../../../../../public/assets/js/feather.min";
import debounce from 'lodash.debounce'
import dayjs from 'dayjs'

export default {
  setup() {
    const toast = useToast()
    return {toast}
  },

  data() {
    return {
      filters: {
        start_date: "",
        end_date: "",
        account_id: "",
      },
      errors: {},
      results:[],
      query:'',
      debouncedSearch:null,
      itemIndex:null,
      selectedItem:null,
      opening:null,
      opening_balance:0,
      ledgers:[],
      runing_balance:0,
    };
  },
  created() {
    this.debouncedSearch = debounce(this.searchItems, 300)
  },

  methods: {
    dateFormat(date){
      if(!date) return;
      return dayjs(date).format('DD-MM-YYYY')
    },

    async getHeadName(account_id) {
      const response = await this.$axios.get(apiUrls.chart_of_accounts.get, {
            params: {status:'Child' }
          })
        const head = response.data.find(item => item.account_id == account_id)
        return head ? head.node : ''
    },
  	selectItem(item) {
      console.log('SelectItem:::',item)
      this.selectedItem = item
      this.query = item.node
      this.filters.account_id = item.account_id
      this.results = []
      this.itemIndex = 0;
    },
  	async searchItems() {
        if (!this.query) {
          this.results = []
          return
        }

        try {
          const response = await this.$axios.get(apiUrls.chart_of_accounts.get, {
            params: {q: this.query, status:'Child' }
          })
          const results = response.data || [];
          this.results = results;
          this.itemIndex = 0;

          await this.$nextTick(() => {
            if (window.feather) feather.replace()
          })
        } catch (error) {
          console.error('Search error:', error)
          this.results = []
        }
      },
  	handleKeys(event) {
        if (event.key === 'ArrowDown') {
          this.itemIndex = Math.min(this.itemIndex + 1, this.results.length - 1);
          event.preventDefault();
        } else if (event.key === 'ArrowUp') {
          this.itemIndex = Math.max(this.itemIndex - 1, 0);
          event.preventDefault();
        } else if (event.key === 'Enter') {
          const selectedProduct = this.results[this.itemIndex];
          if (selectedProduct) {
            this.selectItem(selectedProduct);
          }
          event.preventDefault();
        }
      },
  	  async submitReport() {
        if(!this.filters.account_id) return;

        try {

          const response = await this.$axios.get(apiUrls.report_ledger.get, {
              params: this.filters
            });
          console.log(response)
          this.opening = response.data.opening
          this.opening_balance = response.data.opening_balance
          this.ledgers = response.data.data
          this.runing_balance = response.data.opening_balance

          let balance = response.data.opening_balance
          this.ledgers = this.ledgers.map(item => {
              balance = parseFloat(balance) - parseFloat(item.debit) + parseFloat(item.credit)

              return {
                  ...item,
                  balance: balance
              }
          })

        } catch (error) {
          console.log(error)
        }
      },

    resetForm() {
      this.filters = {
        start_date: "",
        end_date: "",
        item_code: "",
      };
      this.errors = {};
    },
  }
}

</script>

<style scoped>
caption{
  font-size:18px !important;
}
</style>